import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';

/**
 * Public, unauthenticated document page (BUI-989) — linked from the Quick Scan
 * consent step (apps/web/src/pages/QuickScan.tsx) and reachable directly at
 * /terms without signing in, same as /auth/sso/callback (see App.tsx).
 */
export function Terms() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <PageHeader
        title="Security Testing Terms &amp; Conditions"
        description="Please read this in full before authorizing any security test through DefendIQ."
      />
      <Card>
        <CardContent className="space-y-8 pt-6 text-sm leading-6 text-ink-muted">
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-ink">1. Authorized use only</h2>
            <p>
              DefendIQ ("the Platform") may only be used to test assets — web applications and APIs, public-facing
              domains/subdomains/IPs, and internal services reached through a customer-installed broker — that you own or
              have explicit, documented, written authorization to test from the asset owner. Every scan requires an
              active, in-window authorization record and a valid scope policy before any testing activity begins. Testing
              any asset without such authorization is a violation of these terms and may violate the Computer Fraud and
              Abuse Act, the UK Computer Misuse Act, and equivalent laws in your jurisdiction.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-semibold text-ink">2. Your responsibility and acceptance of liability</h2>
            <p>
              By accepting these terms and starting a test, you personally confirm that you are authorized to initiate
              testing against the specified target, and you accept liability for any consequences of testing an asset you
              were not, in fact, authorized to test. You are responsible for keeping evidence of that authorization
              (e.g. a signed authorization document, a client engagement letter, or equivalent) and for producing it on
              request.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-semibold text-ink">3. BUI's liability disclaimer</h2>
            <p>
              BUI (Pty) Ltd ("BUI") provides the Platform's testing modules on a non-destructive, capability-limited,
              policy-gated basis, enforced by allowlists, rate limits, scan windows, and a kill switch. Nonetheless,
              security testing carries inherent risk. To the maximum extent permitted by law, BUI accepts no liability
              for any service disruption, data loss, or system instability arising from a security test initiated through
              the Platform, whether or not that test was properly authorized. BUI's modules are never destructive by
              design and never contain exploit payloads or malware — but no automated testing can guarantee zero impact
              on a live system.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-semibold text-ink">4. Data handling</h2>
            <p>
              Findings and evidence collected during a test are encrypted at rest and are only accessible within your
              tenant, subject to your workspace's role-based access controls. Every access and mutation to a finding,
              authorization, or scope policy is recorded in an immutable, hash-chained audit log. You may request
              deletion of your tenant's findings, reports, and consent records at any time by contacting your BUI account
              representative; deletion requests are honored subject to any legal or contractual retention obligations
              that may apply.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-semibold text-ink">5. Acceptable use</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>Do not use the Platform to test assets outside the scope policy you configured for an engagement.</li>
              <li>Do not attempt to disable, bypass, or circumvent the kill switch, rate limits, or scan windows.</li>
              <li>Do not use findings or access gained through the Platform for any purpose other than remediating your own systems.</li>
              <li>Do not share your workspace credentials or API keys outside your organization.</li>
              <li>Report any suspected misuse of the Platform to your BUI account representative immediately.</li>
            </ul>
          </section>

          <p className="border-t border-border pt-6 text-xs text-ink-faint">
            Version 2026-07-16. These terms may be updated from time to time; material changes will require re-acceptance
            before starting a new test.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
